import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';

import Stack from '@/design_system/Stack';
import {
  getCurrentAndTotalStep,
  Statuses,
  Stepper,
  StepperProps,
} from '@/design_system/Stepper/Stepper';
import { ARTICLE_DISPLAYED_STEPS, ARTICLE_TASK_TYPES, getDisplayedStep } from '@/models/article';
import { ArticleWithRelations } from '@/models/request';
import useViewPort from '@/utils/useViewport';

export const ArticleStepper = ({
  article,
  mode,
  withoutStepper,
  ...props
}: {
  article: ArticleWithRelations;
  mode: 'task' | 'step';
  withoutStepper?: boolean;
} & Omit<StepperProps, 'steps'>) => {
  const { _ } = useLingui();
  const { isMobile } = useViewPort();

  if (!article.steps) {
    return '-';
  }

  const steps = article.steps
    .map((step) => {
      const displayedStep = getDisplayedStep(step);

      if (!displayedStep || displayedStep === 'completed') {
        return null;
      }

      const name = _(ARTICLE_DISPLAYED_STEPS.find(({ id }) => id === displayedStep)!.name);
      let label;

      if (mode === 'task' && article.taskType && step.status === 'in-progress') {
        label = (
          <Stack row alignItems="center" gap="0.25rem" flexWrap="nowrap">
            <span>{_(ARTICLE_TASK_TYPES.find(({ id }) => id === article.taskType)!.label)}</span>
          </Stack>
        );
      }

      return {
        ...step,
        name,
        label,
      };
    })
    .filter((step) => !!step);

  if (!steps.length) {
    return '-';
  }

  const labelVariant = mode === 'task' ? 'pending' : isMobile ? 'awaiting' : undefined;

  const { currentStepIndex } = getCurrentAndTotalStep(steps);

  if (withoutStepper) {
    const step = steps[currentStepIndex];

    return (
      <span
        aria-label={_(
          msg({
            id: 'stepper.step.label',
            message: `Step ${step.name}: ${_(Statuses[step.status])}`,
          })
        )}
      >
        {step.label ?? step.name}
      </span>
    );
  }

  return <Stepper steps={steps} labelVariant={labelVariant} {...props} />;
};
